/**
 * Zone containment rules (AVA-764: "document the geometry used to identify candidates").
 *
 * - A boundary is a 2D polygon in site metres plus a vertical range [zMin, zMax].
 * - A record is tested by its single stored position. This does NOT represent the full extent
 *   of extended objects (pipes, structures); results are review candidates, never membership.
 * - Points on an edge or vertex, or exactly on zMin/zMax, count as inside (EDGE_TOLERANCE_M).
 * - Records without a position are never candidates (they can still be assigned explicitly).
 * - Overlapping zones are evaluated independently, so a point can be a candidate for several.
 *
 * In the real build this runs server-side (see .claude/guides/MCP_READINESS.md).
 */

export const EDGE_TOLERANCE_M = 0.001;

function distanceToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function isPointOnPolygonEdge(point, polygon) {
  return polygon.some((a, i) => distanceToSegment(point, a, polygon[(i + 1) % polygon.length]) <= EDGE_TOLERANCE_M);
}

// Ray casting; edges are handled separately so the result is not ambiguous on the boundary.
export function isPointInPolygon(point, polygon) {
  if (!polygon || polygon.length < 3) return false;
  if (isPointOnPolygonEdge(point, polygon)) return true;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    if ((a.y > point.y) !== (b.y > point.y) && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

export function isPositionInBoundary(position, boundary) {
  if (!position || !boundary) return false;
  if (position.z < boundary.zMin - EDGE_TOLERANCE_M || position.z > boundary.zMax + EDGE_TOLERANCE_M) return false;
  return isPointInPolygon(position, boundary.polygon);
}

export function polygonBounds(polygon) {
  return polygon.reduce(
    (b, p) => ({ minX: Math.min(b.minX, p.x), minY: Math.min(b.minY, p.y), maxX: Math.max(b.maxX, p.x), maxY: Math.max(b.maxY, p.y) }),
    { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
  );
}

export function polygonArea(polygon) {
  let sum = 0;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    sum += (polygon[j].x + polygon[i].x) * (polygon[j].y - polygon[i].y);
  }
  return Math.abs(sum / 2);
}
